import {Box, Divider, Flex, Text, Title} from 'exsportia-components'
import {useFormikContext} from 'formik'
import {FC, useCallback, useEffect, useMemo} from 'react'
import {useQuery} from 'react-query'
import {useNavigate, useParams} from 'react-router-dom'

import {MemberQueries} from '../../api/member'
import {SubscriptionBookingsQueries} from '../../api/subscription-bookings'
import {SubscriptionBooking, SubscriptionBookingStatusesEnum} from '../../api/subscription-bookings/types'
import {EmptyList} from '../../components/empty-list/empty-list'
import {noop} from '../../helpers/logic'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {TranslatorScope} from '../../locale/types'
import {useHandleLoginState} from '../../services/auth/hooks'
import {PaySubscriptionCycleFormType} from '../../services/subscription-bookings/forms/pay-subscription-cycles-form'
import {VenueService} from '../../services/venue'
import {theme} from '../../theme/theme'
import {NavigationList} from '../../ui/components/navigation-list/navigation-list'
import {auth} from '../../utils/firebase'
import {useLocale, useLuxon} from '../../utils/i18n'
import {isNilOrEmpty, isNotNilAndNotEmpty} from '../../utils/logic'
import {
  SubscriptionBookingCardWithChips,
  SubscriptionBookingCardWithChipsProps,
} from './components/subscription-booking-card-with-chips'

const durationTypesLocalesMap = {
  DAYS: 'days',
  WEEK: 'weeks',
  MONTH: 'months',
}

const getSubscriptionBookingPayDate = (booking: SubscriptionBooking) =>
  booking.cycles.reduce((acc, cur) => {
    if (cur.isPaid) {
      return acc
    }
    return cur.payDate < acc ? cur.payDate : acc
  }, Infinity)

// type tabType = 'subscriptions'

type SubscriptionBookingsMapType = {
  [key in SubscriptionBookingStatusesEnum]: SubscriptionBooking[]
}

const categoryTitlesMap: {[key in SubscriptionBookingStatusesEnum]: TranslatorScope} = {
  OTHER: 'other',
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  FROZEN: 'frozen',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  AWAITING_PAYMENT: 'awaitingPayment',
  PAYMENT_FAILED: 'paymentFailed',
}

// const Header: FC<{tabSettings: TabButtonProps[]}> = ({tabSettings}) => {
//   const {l} = useLocale()
//   return (
//     <>
//       <Flex
//         display={['flex', 'none']}
//         p='16px 16px 0 16px'
//       >
//         <Title text={l('bookings')} />
//       </Flex>
//       <NavMenu tabsSettings={tabSettings} />
//       <Divider settings={{m: 0}} />
//     </>
//   )
// }

const SubscriptionsCategory: FC<{
  list: SubscriptionBooking[] | []
  title: string
  venueName: string
  category: keyof typeof categoryTitlesMap
}> = ({list, venueName, title, category}) => {
  const luxon = useLuxon()
  const {l} = useLocale()
  const navigate = useNavigate()
  const now = luxon.now()
  const {setFieldValue} = useFormikContext<PaySubscriptionCycleFormType>()
  if (isNilOrEmpty(list)) {
    return null
  }
  return (
    <Flex
      flexDirection='column'
      mb='8px'
    >
      <Title
        text={title}
        styleType='h2'
        settings={{
          mt: '16px',
          ml: '16px',
          mb: '8px',
        }}
      />
      <NavigationList
        withoutChevron={true}
        elementsSettings={list}
        Component={(data: SubscriptionBooking) => {
          // TODO: use separated component
          const chips: SubscriptionBookingCardWithChipsProps['chips'] = []
          const expireDate = luxon.fromMillis(data.expireDate || data.endDate || 0)
          const daysToExpire = expireDate.diff(now).as('days')
          if (daysToExpire > 0 && daysToExpire < 7 && category !== 'OTHER') {
            chips.push({
              text: l('expiringSoon', {days: daysToExpire}),
              color: theme.colors['bg-expiring'],
              textColor: theme.colors['text-contrast'],
            })
          }
          if (data.subStatus === 'AWAITING_PAYMENT') {
            chips.push({
              text: l('awaitingPayment'),
              color: theme.colors.notify,
              textColor: theme.colors['text-contrast'],
            })
          }
          chips.push(
            data.status === 'ACTIVE'
              ? {
                  text: l('active'),
                  color: theme.colors.success,
                  textColor: theme.colors['text-contrast'],
                }
              : {
                  text: l('inactive'),
                  color: theme.colors.error,
                  textColor: theme.colors['text-contrast'],
                },
          )
          if (data.subStatus === 'FROZEN') {
            chips.push({
              text: l('frozen'),
              color: theme.colors['bg-frozen'],
              textColor: theme.colors['text-contrast'],
            })
          }
          if (data.subStatus === 'CANCELLED') {
            chips.push({
              text: l('cancelled'),
              color: theme.colors.error,
              textColor: theme.colors['text-contrast'],
            })
          }
          if (data.subStatus === 'EXPIRED') {
            chips.push({
              text: l('expired'),
              color: theme.colors.error,
              textColor: theme.colors['text-contrast'],
            })
          }
          chips.push({
            textColor: theme.colors['text-contrast'],
            color: 'rgba(0, 16, 61, 0.48)',
            text: l(`${durationTypesLocalesMap[data.durationType]}_single` as TranslatorScope, {
              value: data.duration,
              declension: [
                `${durationTypesLocalesMap[data.durationType]}_single_genitive`,
                `${durationTypesLocalesMap[data.durationType]}_plural`,
              ] as [TranslatorScope, TranslatorScope],
            }),
          })
          if (isNotNilAndNotEmpty(data.usageStatuses)) {
            if (data.usageStatuses.includes('RUN_OUT')) {
              chips.push({
                text: l('runOut'),
                color: theme.colors['bg-info-tag'],
                textColor: theme.colors.error,
              })
            } else {
              chips.push({
                text: l('runningOut'),
                color: theme.colors['bg-expiring'],
                textColor: theme.colors['text-contrast'],
              })
            }
          }

          return (
            <Box
              width={'100%'}
              onClick={() => {
                setFieldValue('subscriptionBooking', data).catch((e) => console.log(String(e)))
                navigate(`./${data.uid}/profile`)
              }}
            >
              <SubscriptionBookingCardWithChips
                chips={chips}
                booking={data}
                subtitle={
                  <Flex>
                    <Text
                      text={`at ${venueName}`}
                      settings={{color: theme.colors['text-contrast']}}
                    />
                    <Flex>
                      <Divider
                        settings={{bg: theme.colors['text-contrast']}}
                        styleType='dot'
                      />
                      <Text
                        text={`${l('validUntil')} ${expireDate.toFormat('MMM dd, yyyy')}`}
                        settings={{color: theme.colors['text-contrast']}}
                      />
                    </Flex>
                  </Flex>
                }
              />
            </Box>
          )
        }}
      />
    </Flex>
  )
}

const SubscriptionBookingsList: FC = () => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const {venueId} = useParams<{venueId: string}>()
  // const {isLoggedIn} = useAuthState()
  const userId = auth.currentUser?.uid
  // const [prevIsLogged, setPrevIsLogged] = useState(isLoggedIn)

  const handleLogOut = useCallback(() => navigate('../'), [])

  useHandleLoginState({
    handleLogOut,
  })
  const {data: venueData, isLoading: isVenueDataLoading} = useQuery(
    VenueService.queries.getVenueQuery({params: {id: venueId}}),
  )
  const {data: member, isLoading: isMemberDataLoading} = useQuery(
    MemberQueries.getMemberByVenueQuery({
      query: {
        userId,
        venueId,
      },
    }),
  )

  const {
    data: subscriptionBookings,
    isLoading: isSubscriptionBookingsLoading,
    refetch,
  } = useQuery(
    [`getSubscriptionBookingsByMemberQuery${venueId}${member?.uid}`],
    // eslint-disable-next-line
    SubscriptionBookingsQueries.getSubscriptionBookingsByMemberQuery({
      params: {
        venueId,
        memberId: member?.uid,
      },
    }).queryFn!,
    {
      enabled: !!member?.uid,
    },
  )
  const isLoading = useMemo(
    () => isMemberDataLoading || isSubscriptionBookingsLoading || isVenueDataLoading,
    [isMemberDataLoading, isSubscriptionBookingsLoading, isVenueDataLoading],
  )
  useEffect(() => {
    refetch()
  }, [member?.uid, refetch])
  /* useEffect(() => {
    if (prevIsLogged && !isLoggedIn) {
      navigate('../')
    }
    setPrevIsLogged(isLoggedIn)
  }, [isLoggedIn])*/

  const subscriptionBookingsMap = useMemo(() => {
    if (isNilOrEmpty(subscriptionBookings)) {
      return null
    }
    const subscriptionBookingsByType = subscriptionBookings.reduce<SubscriptionBookingsMapType>(
      (acc, cur) => {
        if (cur.status === 'ACTIVE') {
          return {
            ...acc,
            ACTIVE: [
              ...acc.ACTIVE,
              {
                ...cur,
                onClick: noop,
              },
            ],
          }
        }
        if (cur.subStatus && Object.keys(acc).includes(cur.subStatus)) {
          return {
            ...acc,
            [cur.subStatus]: [
              ...acc[cur.subStatus],
              {
                ...cur,
                onClick: noop,
              },
            ],
          }
        }
        return {
          ...acc,
          OTHER: [
            ...acc.OTHER,
            {
              ...cur,
              onClick: noop,
            },
          ],
        }
      },
      {
        OTHER: [],
        ACTIVE: [],
        INACTIVE: [],
        FROZEN: [],
        EXPIRED: [],
        CANCELLED: [],
        AWAITING_PAYMENT: [],
        PAYMENT_FAILED: [],
      },
    )
    return {
      ACTIVE: subscriptionBookingsByType.ACTIVE.sort((a, b) => {
        const aExpireDate = a.expireDate || 0
        const bExpireDate = b.expireDate || 0
        return aExpireDate - bExpireDate
      }),
      INACTIVE: subscriptionBookingsByType.INACTIVE.sort((a, b) => {
        const aExpireDate = a.expireDate || 0
        const bExpireDate = b.expireDate || 0
        return aExpireDate - bExpireDate
      }),
      FROZEN: subscriptionBookingsByType.FROZEN.sort((a, b) => {
        const aUnfreezeDate = a.currentFreezePeriod?.endDate || 0
        const bUnfreezeDate = b.currentFreezePeriod?.endDate || 0
        return bUnfreezeDate - aUnfreezeDate
      }),
      AWAITING_PAYMENT: subscriptionBookingsByType.AWAITING_PAYMENT.sort((a, b) => {
        const aPayDate = getSubscriptionBookingPayDate(a)
        const bPayDate = getSubscriptionBookingPayDate(b)
        return bPayDate - aPayDate
      }),
      CANCELLED: subscriptionBookingsByType.CANCELLED.sort((a, b) => {
        const aExpireDate = a.cancel_timestamp || 0
        const bExpireDate = b.cancel_timestamp || 0
        return bExpireDate - aExpireDate
      }),
      EXPIRED: subscriptionBookingsByType.EXPIRED.sort((a, b) => {
        const aExpireDate = a.expireDate || 0
        const bExpireDate = b.expireDate || 0
        return bExpireDate - aExpireDate
      }),
      OTHER: subscriptionBookingsByType.OTHER,
      PAYMENT_FAILED: subscriptionBookingsByType.PAYMENT_FAILED,
    }
  }, [subscriptionBookings])

  if (subscriptionBookingsMap === null) {
    if (isLoading) {
      return (
        <NavigationList
          isLoading={isLoading}
          Component={() => null}
          elementsSettings={[]}
        />
      )
    }
    return <EmptyList withoutBack />
  }

  return (
    <>
      {Object.keys(subscriptionBookingsMap).map((key) => {
        const keyToUse = key as SubscriptionBookingStatusesEnum
        return (
          <SubscriptionsCategory
            key={key}
            category={keyToUse}
            list={subscriptionBookingsMap[keyToUse]}
            title={l(categoryTitlesMap[keyToUse] as TranslatorScope)}
            venueName={venueData?.name || ''}
          />
        )
      })}
    </>
  )
}

// TODO: just make a variant-component for each possible case
const listToUseMap = {
  subscriptions: SubscriptionBookingsList,
}

export const SubscriptionBookingsListPage: FC = () => {
  // const [activeTab, setActiveTab] = useState<tabType>('subscriptions')
  // const tabSettings: TabButtonProps[] = useMemo(
  //   () => [
  //     {
  //       text: l('subscriptions', {caseAction: 'capitalize'}),
  //       isActive: activeTab === 'subscriptions', // eslint-disable-line
  //       onClick: () => setActiveTab('subscriptions'),
  //     },
  //   ],
  //   [activeTab],
  // )
  const ListToUse = useMemo(() => listToUseMap.subscriptions, [])
  return (
    <GridLayout withoutPadding={true}>
      <ListToUse />
    </GridLayout>
  )
}
